/**
 * Intake Form Prefill Mapper
 *
 * Maps prefill data from backend to the specific form structures used by each intake form
 */

import { PrefillData } from '@/services/intakeService';
import { parseMedicalHistory } from '@/components/ui/medical-history-multi-select';

/**
 * Convert total inches to feet/inches.
 */
function inchesToFeetInches(totalInches: number): { feet: number; inches: number } {
  const normalized = Math.max(0, Math.round(totalInches));
  return {
    feet: Math.floor(normalized / 12),
    inches: normalized % 12,
  };
}

/**
 * Convert height in cm to feet and inches.
 */
function cmToFeetInches(cm: string | number): { feet: number; inches: number } {
  const cmValue = typeof cm === 'string' ? parseFloat(cm) : cm;
  if (!Number.isFinite(cmValue) || cmValue <= 0) return { feet: 0, inches: 0 };
  const totalInches = cmValue / 2.54;
  return inchesToFeetInches(totalInches);
}

/**
 * Convert height value to feet/inches.
 * Handles various formats: "180", "180cm", "5'11\"", etc.
 */
function parseHeight(height?: string | number): { feet?: number; inches?: number; cm?: number } {
  if (height === undefined || height === null || height === '') return {};
  const heightText = String(height).trim();
  if (!heightText) return {};

  // Check if explicitly in feet/inches format (requires ft or ' separator)
  const feetInchesMatch = heightText.match(/^\s*(\d{1,2})\s*(?:'|ft)\s*(\d{1,2})\s*(?:"|in)?\s*$/i);
  if (feetInchesMatch) {
    const feet = parseInt(feetInchesMatch[1], 10);
    const inches = parseInt(feetInchesMatch[2], 10);
    if (!Number.isFinite(feet) || !Number.isFinite(inches) || feet < 0 || inches < 0) return {};
    const normalized = inchesToFeetInches(feet * 12 + inches);
    return {
      feet: normalized.feet,
      inches: normalized.inches,
    };
  }

  // Parse plain numeric values.
  // Heuristic:
  // - <= 96 is likely inches (e.g., "75" => 6'3")
  // - > 96 is treated as cm (e.g., "180" => 5'11")
  const numericValue = parseFloat(heightText.replace(/[^\d.]/g, ''));
  if (!Number.isNaN(numericValue) && Number.isFinite(numericValue) && numericValue > 0) {
    if (numericValue <= 96) {
      const normalized = inchesToFeetInches(numericValue);
      return {
        feet: normalized.feet,
        inches: normalized.inches,
      };
    }

    const { feet, inches } = cmToFeetInches(numericValue);
    return { feet, inches, cm: numericValue };
  }

  return {};
}

function formatHeight(feet?: number, inches?: number): string | undefined {
  if (!Number.isFinite(feet as number) && !Number.isFinite(inches as number)) return undefined;
  const safeFeet = Number.isFinite(feet as number) ? Math.max(0, Math.floor(feet as number)) : 0;
  const safeInches = Number.isFinite(inches as number) ? Math.max(0, Math.floor(inches as number)) : 0;
  const normalized = inchesToFeetInches(safeFeet * 12 + safeInches);
  return `${normalized.feet}'${normalized.inches}"`;
}

/**
 * Extract date from ISO datetime string
 * Converts "1994-01-12T00:00:00.000Z" to "1994-01-12"
 */
function extractDate(isoDateString?: string): string {
  if (!isoDateString) return '';
  // Extract YYYY-MM-DD from ISO datetime
  return isoDateString.split('T')[0];
}

/**
 * Map billing and shipping address fields from prefill data.
 * Returns flat fields for both addresses plus a billingSameAsShipping flag.
 */
function mapBillingShippingAddresses(prefillData: PrefillData) {
  const result: Record<string, string | boolean> = {};

  // Billing address (primary destination for the form)
  const bLine1 = prefillData.billing_address_line_1 || prefillData.address_line_1;
  const bLine2 = prefillData.billing_address_line_2 || prefillData.address_line_2;
  const bCity = prefillData.billing_city || prefillData.city;
  const bState = prefillData.billing_state || prefillData.state;
  const bZip = prefillData.billing_zip_code || prefillData.zip_code;

  if (bLine1) result.billingAddressLine1 = bLine1;
  if (bLine2) result.billingAddressLine2 = bLine2;
  if (bCity) result.billingCity = bCity;
  if (bState) result.billingState = bState;
  if (bZip) result.billingPostalCode = bZip;

  // Shipping address
  const sLine1 = prefillData.shipping_address_line_1 || bLine1;
  const sLine2 = prefillData.shipping_address_line_2 || bLine2;
  const sCity = prefillData.shipping_city || bCity;
  const sState = prefillData.shipping_state || bState;
  const sZip = prefillData.shipping_zip_code || bZip;

  if (sLine1) result.shippingAddressLine1 = sLine1;
  if (sLine2) result.shippingAddressLine2 = sLine2;
  if (sCity) result.shippingCity = sCity;
  if (sState) result.shippingState = sState;
  if (sZip) result.shippingPostalCode = sZip;

  // Toggle: ON if both addresses are identical (or shipping wasn't set separately)
  const addressesMatch =
    (sLine1 || '') === (bLine1 || '') &&
    (sLine2 || '') === (bLine2 || '') &&
    (sCity || '') === (bCity || '') &&
    (sState || '') === (bState || '') &&
    (sZip || '') === (bZip || '');
  result.billingSameAsShipping = addressesMatch;

  return result;
}

// ============================================================================
// PatientIntake Form Mapper (General Intake - 5 Steps)
// ============================================================================

export interface PatientIntakeFormData {
  // Step 1: Basic Information
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  biological_sex?: string;
  city?: string;
  state?: string;
  zip_code?: string;

  // Step 2: Medical History
  conditions?: string[];
  medications?: string;
  allergies?: string[];
  surgeries?: string;
  family_history?: string;
  other?: string;

  // Step 3: Questionnaires (ADAM, STOP-Bang) - not prefilled

  // Step 4: Lifestyle
  exercise_frequency?: string;
  diet_quality?: string;
  sleep_hours?: string;
  alcohol_frequency?: string;
  smoking_status?: string;
  stress_level?: string;
  goals?: string;

  // Step 5: Billing/Shipping Address (pre-filled from patient address)
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingPostalCode?: string;
  shippingAddressLine1?: string;
  shippingAddressLine2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  billingSameAsShipping?: boolean;
}

export function mapPrefillToPatientIntake(prefillData: PrefillData): Partial<PatientIntakeFormData> {
  const mapped: Partial<PatientIntakeFormData> = {};

  // Basic info
  if (prefillData.first_name) mapped.first_name = prefillData.first_name;
  if (prefillData.last_name) mapped.last_name = prefillData.last_name;
  if (prefillData.email) mapped.email = prefillData.email;
  if (prefillData.phone) mapped.phone = prefillData.phone;
  if (prefillData.dob) mapped.date_of_birth = extractDate(prefillData.dob);
  if (prefillData.biological_sex) mapped.biological_sex = prefillData.biological_sex;
  if (prefillData.city) mapped.city = prefillData.city;
  if (prefillData.state) mapped.state = prefillData.state;
  if (prefillData.zip_code) mapped.zip_code = prefillData.zip_code;

  // Medical history — explicit conditions field takes priority; otherwise parse JSON medical_history.
  if (prefillData.conditions) {
    mapped.conditions = typeof prefillData.conditions === 'string'
      ? prefillData.conditions.split(',').map(c => c.trim()).filter(Boolean)
      : [];
  } else if (prefillData.medical_history) {
    const parsed = parseMedicalHistory(prefillData.medical_history);
    if (parsed.conditions.length > 0) mapped.conditions = parsed.conditions;
    if (parsed.other) mapped.other = parsed.other;
  }
  if (prefillData.medications) mapped.medications = prefillData.medications;
  if (prefillData.allergies) {
    mapped.allergies = typeof prefillData.allergies === 'string'
      ? prefillData.allergies.split(',').map(a => a.trim()).filter(Boolean)
      : [];
  }
  if (prefillData.surgeries) mapped.surgeries = prefillData.surgeries;
  if (prefillData.family_history || prefillData.familyHistory) {
    mapped.family_history = prefillData.family_history || prefillData.familyHistory;
  }

  // Lifestyle
  if (prefillData.exercise_frequency || prefillData.exerciseFrequency) {
    mapped.exercise_frequency = prefillData.exercise_frequency || prefillData.exerciseFrequency;
  }
  if (prefillData.diet_quality || prefillData.diet) {
    mapped.diet_quality = prefillData.diet_quality || prefillData.diet;
  }
  if (prefillData.sleep_hours || prefillData.sleepHours) {
    mapped.sleep_hours = prefillData.sleep_hours || prefillData.sleepHours;
  }
  if (prefillData.alcohol_frequency || prefillData.alcoholConsumption) {
    mapped.alcohol_frequency = prefillData.alcohol_frequency || prefillData.alcoholConsumption;
  }
  if (prefillData.smoking_status || prefillData.smoking) {
    mapped.smoking_status = prefillData.smoking_status || prefillData.smoking;
  }
  if (prefillData.stress_level || prefillData.stressLevel) {
    mapped.stress_level = prefillData.stress_level || prefillData.stressLevel;
  }

  // Billing & shipping addresses (pre-fill from patient address)
  const addresses = mapBillingShippingAddresses(prefillData);
  Object.assign(mapped, addresses);

  return mapped;
}

// ============================================================================
// WomensIntake Form Mapper (Women's Health - 7 Steps)
// ============================================================================

export interface WomensIntakeFormData {
  // Step 2: Personal Info
  fullName?: string;
  email?: string;
  phone?: string;
  state?: string;
  age?: number | null;
  dateOfBirth?: string;
  heightFeet?: number | null;
  heightInches?: number | null;
  weight?: number | null;

  // Step 5: Medical History
  medications?: string[];
  otherMedications?: string;
  allergies?: string[];
  medicalConditions?: string[];
  medicalConditionsOther?: string;
  additionalMedicalHistory?: string;

  // Billing/Shipping Address (pre-filled from patient address)
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingPostalCode?: string;
  shippingAddressLine1?: string;
  shippingAddressLine2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  billingSameAsShipping?: boolean;
}

export function mapPrefillToWomensIntake(prefillData: PrefillData): Partial<WomensIntakeFormData> {
  const mapped: Partial<WomensIntakeFormData> = {};

  // Personal info
  if (prefillData.first_name && prefillData.last_name) {
    mapped.fullName = `${prefillData.first_name} ${prefillData.last_name}`.trim();
  } else if (prefillData.first_name) {
    mapped.fullName = prefillData.first_name;
  }
  if (prefillData.email) mapped.email = prefillData.email;
  if (prefillData.phone) mapped.phone = prefillData.phone;
  if (prefillData.state) mapped.state = prefillData.state;
  if (prefillData.dob) {
    const dateOnly = extractDate(prefillData.dob);
    mapped.dateOfBirth = dateOnly;
    // Calculate age from DOB
    const birthDate = new Date(dateOnly);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    mapped.age = age;
  }

  // Height and weight
  const feetFromPrefill = Number(prefillData.heightFeet);
  const inchesFromPrefill = Number(prefillData.heightInches);
  if (Number.isFinite(feetFromPrefill)) mapped.heightFeet = feetFromPrefill;
  if (Number.isFinite(inchesFromPrefill)) mapped.heightInches = inchesFromPrefill;
  if (mapped.heightFeet == null && mapped.heightInches == null && prefillData.height) {
    const { feet, inches } = parseHeight(prefillData.height);
    if (feet !== undefined) mapped.heightFeet = feet;
    if (inches !== undefined) mapped.heightInches = inches;
  }
  if (prefillData.weight !== undefined && prefillData.weight !== null && prefillData.weight !== '') {
    const w = Number(prefillData.weight);
    if (Number.isFinite(w)) mapped.weight = w;
  }

  // Medical history
  if (prefillData.medications || prefillData.currentMedications) {
    const medsRaw = prefillData.medications || prefillData.currentMedications || '';
    if (Array.isArray(medsRaw)) {
      mapped.medications = medsRaw;
    } else if (typeof medsRaw === 'string' && medsRaw.trim()) {
      // Legacy freeform text goes into otherMedications for manual review
      mapped.otherMedications = medsRaw;
    }
  }
  if (prefillData.allergies) {
    mapped.allergies = Array.isArray(prefillData.allergies)
      ? prefillData.allergies.filter(Boolean)
      : String(prefillData.allergies).split(',').map(s => s.trim()).filter(Boolean);
  }
  const conditionsRaw = prefillData.conditions || prefillData.medicalConditions;
  if (conditionsRaw) {
    mapped.medicalConditions = Array.isArray(conditionsRaw)
      ? conditionsRaw.filter(Boolean)
      : String(conditionsRaw).split(',').map(s => s.trim()).filter(Boolean);
  } else if (prefillData.medical_history) {
    const parsed = parseMedicalHistory(prefillData.medical_history);
    if (parsed.conditions.length > 0) mapped.medicalConditions = parsed.conditions;
    if (parsed.other) mapped.medicalConditionsOther = parsed.other;
  }

  // Billing & shipping addresses (pre-fill from patient address)
  const addresses = mapBillingShippingAddresses(prefillData);
  Object.assign(mapped, addresses);

  return mapped;
}

// ============================================================================
// MensIntake Form Mapper (Men's Health/TRT - 7 Steps)
// ============================================================================

export interface MensIntakeFormData {
  // Step 2: Personal Info
  fullName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  heightFeet?: number | null;
  heightInches?: number | null;
  weight?: string;
  state?: string;

  // Step 5: Medical History
  currentMedications?: string;
  allergies?: string[];
  chronicConditions?: string[];
  chronicConditionsOther?: string;
  surgeries?: string;
  familyHistory?: string;

  // Step 6: Lifestyle
  activityLevel?: string;
  exerciseFrequency?: string;
  sleepHours?: string;
  sleepQuality?: string;
  stressLevel?: string;
  alcoholConsumption?: string;
  tobaccoUse?: string;

  // Step 7: Billing/Shipping Address (pre-filled from patient address)
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingPostalCode?: string;
  shippingAddressLine1?: string;
  shippingAddressLine2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  billingSameAsShipping?: boolean;
}

export function mapPrefillToMensIntake(prefillData: PrefillData): Partial<MensIntakeFormData> {
  const mapped: Partial<MensIntakeFormData> = {};

  // Personal info
  if (prefillData.first_name && prefillData.last_name) {
    mapped.fullName = `${prefillData.first_name} ${prefillData.last_name}`.trim();
  } else if (prefillData.first_name) {
    mapped.fullName = prefillData.first_name;
  }
  if (prefillData.email) mapped.email = prefillData.email;
  if (prefillData.phone) mapped.phone = prefillData.phone;
  if (prefillData.dob) mapped.dateOfBirth = extractDate(prefillData.dob);
  if (prefillData.state) mapped.state = prefillData.state;

  // Height (split into feet/inches) and weight
  const feetFromPrefill = Number(prefillData.heightFeet);
  const inchesFromPrefill = Number(prefillData.heightInches);
  if (Number.isFinite(feetFromPrefill) || Number.isFinite(inchesFromPrefill)) {
    if (Number.isFinite(feetFromPrefill)) mapped.heightFeet = feetFromPrefill;
    if (Number.isFinite(inchesFromPrefill)) mapped.heightInches = inchesFromPrefill;
  } else if (prefillData.height !== undefined && prefillData.height !== null && prefillData.height !== '') {
    const { feet, inches } = parseHeight(prefillData.height);
    if (feet !== undefined) mapped.heightFeet = feet;
    if (inches !== undefined) mapped.heightInches = inches;
  }
  if (prefillData.weight) mapped.weight = prefillData.weight.toString();

  // Medical history
  if (prefillData.medications || prefillData.currentMedications) {
    mapped.currentMedications = prefillData.medications || prefillData.currentMedications;
  }
  if (prefillData.allergies) {
    mapped.allergies = Array.isArray(prefillData.allergies)
      ? prefillData.allergies.filter(Boolean)
      : String(prefillData.allergies).split(',').map(s => s.trim()).filter(Boolean);
  }
  const mensConditionsRaw = prefillData.conditions || prefillData.chronicConditions;
  if (mensConditionsRaw) {
    mapped.chronicConditions = Array.isArray(mensConditionsRaw)
      ? mensConditionsRaw.filter(Boolean)
      : String(mensConditionsRaw).split(',').map(s => s.trim()).filter(Boolean);
  } else if (prefillData.medical_history) {
    const parsed = parseMedicalHistory(prefillData.medical_history);
    if (parsed.conditions.length > 0) mapped.chronicConditions = parsed.conditions;
    if (parsed.other) mapped.chronicConditionsOther = parsed.other;
  }
  if (prefillData.surgeries) mapped.surgeries = prefillData.surgeries;
  if (prefillData.family_history || prefillData.familyHistory) {
    mapped.familyHistory = prefillData.family_history || prefillData.familyHistory;
  }

  // Lifestyle
  if (prefillData.activityLevel) mapped.activityLevel = prefillData.activityLevel;
  if (prefillData.exercise_frequency || prefillData.exerciseFrequency) {
    mapped.exerciseFrequency = prefillData.exercise_frequency || prefillData.exerciseFrequency;
  }
  if (prefillData.sleep_hours || prefillData.sleepHours) {
    mapped.sleepHours = prefillData.sleep_hours || prefillData.sleepHours;
  }
  if (prefillData.sleepQuality) mapped.sleepQuality = prefillData.sleepQuality;
  if (prefillData.stress_level || prefillData.stressLevel) {
    mapped.stressLevel = prefillData.stress_level || prefillData.stressLevel;
  }
  if (prefillData.alcohol_frequency || prefillData.alcoholConsumption) {
    mapped.alcoholConsumption = prefillData.alcohol_frequency || prefillData.alcoholConsumption;
  }
  if (prefillData.tobacco_use || prefillData.smoking_status || prefillData.smoking) {
    mapped.tobaccoUse = prefillData.tobacco_use || prefillData.smoking_status || prefillData.smoking;
  }

  // Billing & shipping addresses (pre-fill from patient address)
  const addresses = mapBillingShippingAddresses(prefillData);
  Object.assign(mapped, addresses);

  return mapped;
}

// ============================================================================
// WeightLossIntake Form Mapper (GLP-1 - 8 Steps)
// ============================================================================

export interface WeightLossIntakeFormData {
  // Step 1: MDI Readiness (demographics verification with address)
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;

  // Step 2: Goals
  currentWeight?: string;
  heightFeet?: string;
  heightInches?: string;

  // Step 4: Medical Screening
  familyHistory?: string;

  // Step 6: Lifestyle
  allergies?: string;
  currentMedications?: string;

  // Step 8: Billing/Shipping Address (pre-filled from patient address)
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingPostalCode?: string;
  shippingAddressLine1?: string;
  shippingAddressLine2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  billingSameAsShipping?: boolean;
}

export function mapPrefillToWeightLossIntake(prefillData: PrefillData): Partial<WeightLossIntakeFormData> {
  const mapped: Partial<WeightLossIntakeFormData> = {};

  // MDI Readiness - Demographics
  if (prefillData.first_name) mapped.firstName = prefillData.first_name;
  if (prefillData.last_name) mapped.lastName = prefillData.last_name;
  if (prefillData.email) mapped.email = prefillData.email;
  if (prefillData.phone) mapped.phone = prefillData.phone;
  if (prefillData.dob) mapped.dateOfBirth = extractDate(prefillData.dob);
  if (prefillData.biological_sex) mapped.gender = prefillData.biological_sex;
  if (prefillData.address_line_1) mapped.addressLine1 = prefillData.address_line_1;
  if (prefillData.address_line_2) mapped.addressLine2 = prefillData.address_line_2;
  if (prefillData.city) mapped.city = prefillData.city;
  if (prefillData.state) mapped.state = prefillData.state;
  if (prefillData.zip_code) mapped.postalCode = prefillData.zip_code;

  // Goals - Height and weight
  if (prefillData.weight) mapped.currentWeight = prefillData.weight.toString();
  if (prefillData.heightFeet) {
    mapped.heightFeet = prefillData.heightFeet.toString();
  }
  if (prefillData.heightInches) {
    mapped.heightInches = prefillData.heightInches.toString();
  }
  if (!prefillData.heightFeet && !prefillData.heightInches && prefillData.height) {
    const { feet, inches } = parseHeight(prefillData.height);
    if (feet) mapped.heightFeet = feet.toString();
    if (inches) mapped.heightInches = inches.toString();
  }

  // Medical screening
  if (prefillData.family_history || prefillData.familyHistory) {
    mapped.familyHistory = prefillData.family_history || prefillData.familyHistory;
  }

  // Lifestyle
  if (prefillData.allergies) mapped.allergies = prefillData.allergies;
  if (prefillData.medications || prefillData.currentMedications) {
    mapped.currentMedications = prefillData.medications || prefillData.currentMedications;
  }

  // Billing & shipping addresses (pre-fill from patient address)
  const addresses = mapBillingShippingAddresses(prefillData);
  Object.assign(mapped, addresses);

  return mapped;
}
